
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar, BarChart3, AlertCircle } from 'lucide-react';
import { ComplianceCalendar } from './ComplianceCalendar';
import { ConsolidatedComplianceCalendar } from './ConsolidatedComplianceCalendar';

interface Filing {
  return_type: string;
  date_of_filing: string;
  financial_year: string;
  tax_period: string;
  status: string;
}

interface GSTDetails {
  gstin: string;
  status: string;
  company_name: string;
  trade_name: string;
  state: string;
  filings: Filing[];
}

interface GSTComplianceSectionProps {
  gstDetails: GSTDetails[];
}

export const GSTComplianceSection: React.FC<GSTComplianceSectionProps> = ({ gstDetails }) => {
  const [selectedGSTIN, setSelectedGSTIN] = useState<string>(gstDetails[0]?.gstin || '');
  const [viewMode, setViewMode] = useState<'individual' | 'consolidated'>('consolidated');
  
  // Memoize filtered data for performance
  const selectedCompany = useMemo(() => 
    gstDetails.find(company => company.gstin === selectedGSTIN), 
    [gstDetails, selectedGSTIN]
  );
  
  const { gstr1Filings, gstr3bFilings } = useMemo(() => {
    if (!selectedCompany) return { gstr1Filings: [], gstr3bFilings: [] };
    
    return {
      gstr1Filings: selectedCompany.filings.filter(filing => filing.return_type === 'GSTR1'),
      gstr3bFilings: selectedCompany.filings.filter(filing => filing.return_type === 'GSTR3B')
    };
  }, [selectedCompany]);

  // Error handling for invalid data
  if (!gstDetails || gstDetails.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No GST Details Found</h2>
            <p className="text-gray-600">Please provide GST details to view compliance data.</p>
          </div>
        </div>
      </div>
    );
  }

  return (

    <>
    
    <section>
        <h3 className="text-lg font-medium mb-4">GST Details</h3>
        <div className="flex flex-wrap gap-3">
          {gstDetails.map((detail) => (
            <div 
              key={detail.gstin}
              className="bg-gray-100 rounded-full px-4 py-2 text-sm flex items-center"
            >
              <span className="font-medium">{detail.gstin}</span>
              <span className="mx-2 text-gray-400">|</span>
              <span className={`${detail.status === "Active" ? "text-green-600" : "text-red-600"}`}>
                {detail.status}
              </span>
              <span className="mx-2 text-gray-400">|</span>
              <span>{detail.state}</span>
            </div>
          ))}
        </div>
      </section>
   
    <div className="min-h-screen bg-gray-50 p-6 mt-6">



      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">GST Compliance Dashboard</h1>
              <p className="text-sm text-gray-600">
                Monitoring {gstDetails.length} GST{gstDetails.length > 1 ? 'INs' : 'IN'} across multiple states
              </p>
            </div>
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-white rounded-lg p-1 border border-gray-200">
            <button
              onClick={() => setViewMode('consolidated')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                viewMode === 'consolidated' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              Consolidated View
            </button>
            <button
              onClick={() => setViewMode('individual')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                viewMode === 'individual' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Individual View
            </button>
          </div>
        </div>

        {/* GSTIN Selection - Only show in individual view */}
        {viewMode === 'individual' && (
          <Card className="border-gray-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-medium">Select GSTIN</CardTitle>
            </CardHeader>
            <CardContent>
              <select 
                value={selectedGSTIN} 
                onChange={(e) => setSelectedGSTIN(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                {gstDetails.map((company) => (
                  <option key={company.gstin} value={company.gstin}>
                    {company.gstin} - {company.company_name} ({company.state})
                  </option>
                ))}
              </select>
            </CardContent>
          </Card>
        )}

        {/* Calendar Views */}
        {viewMode === 'consolidated' ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <ConsolidatedComplianceCalendar gstDetails={gstDetails} returnType="GSTR1" />
            <ConsolidatedComplianceCalendar gstDetails={gstDetails} returnType="GSTR3B" />
          </div>
        ) : (
          selectedCompany && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <ComplianceCalendar 
                filings={gstr1Filings} 
                returnType="GSTR1"
                gstin={selectedCompany.gstin}
                state={selectedCompany.state}
                companyName={selectedCompany.company_name}
              />
              <ComplianceCalendar 
                filings={gstr3bFilings} 
                returnType="GSTR3B"
                gstin={selectedCompany.gstin}
                state={selectedCompany.state}
                companyName={selectedCompany.company_name}
              />
            </div>
          )
        )}
      </div>
    </div>
    </>
  );
};
